import type { CSSProperties, ReactNode } from 'react';
import { type ChampionCardRef, isChampionUnitCard } from '../../utils/championIdentity';
import {
  cardRarityGemLabel,
  cardShowsPromoRarityCrown,
  type RarityCardRef,
} from '../../utils/cardRarity';

/** Filenames in `public/rarity-icons/` (without extension). Add new tiers here when assets exist. */
const RARITY_WEBP_SLUGS = [
  'common',
  'uncommon',
  'rare',
  'epic',
  'showcase',
] as const;

export type RarityWebpSlug = (typeof RARITY_WEBP_SLUGS)[number];

const RARITY_SLUG_SET = new Set<string>(RARITY_WEBP_SLUGS);

interface IconSizeProps {
  size?: number;
}

function rarityAssetsPrefix(): string {
  const base = import.meta.env.BASE_URL;
  return base.endsWith('/') ? base : `${base}/`;
}

/** Public URL for a rarity WebP, or null if no asset exists for that tier. */
export function rarityIconSrc(rarityLabel: string): string | null {
  const slug = rarityLabel.trim().toLowerCase();
  if (!RARITY_SLUG_SET.has(slug)) return null;
  return `${rarityAssetsPrefix()}rarity-icons/${slug}.webp`;
}

/** Cream PROMO ribbon that crowns a rarity gem on promo printings. */
export function promoBadgeSrc(): string {
  return `${rarityAssetsPrefix()}rarity-icons/promo-badge.svg`;
}

function RarityAssetImg({
  src,
  size,
  width,
  height,
  title,
  style,
}: {
  src: string;
  size?: number;
  width?: number;
  height?: number;
  title?: string;
  style?: CSSProperties;
}) {
  const w = width ?? size ?? 18;
  const h = height ?? size ?? 18;
  return (
    <img
      src={src}
      alt=""
      width={w}
      height={h}
      decoding="async"
      draggable={false}
      title={title}
      style={{ display: 'block', objectFit: 'contain', flexShrink: 0, ...style }}
    />
  );
}

/** Rarity gem image from `public/rarity-icons/*.webp`. Optional promo ribbon crown. */
export function RarityGemIcon({
  value,
  size = 18,
  promo = false,
  compact = false,
}: IconSizeProps & { value: string; promo?: boolean; compact?: boolean }) {
  const src = rarityIconSrc(value);
  const badge = promo ? promoBadgeSrc() : null;
  if (!src && !badge) return null;

  const title = promo && value ? `Promo ${value}` : promo ? 'Promo' : value;

  if (!promo || !badge) {
    if (!src) return null;
    return <RarityAssetImg src={src} size={size} title={title} />;
  }

  // The SVG includes hanging ribbon tails. Compact chips show only the
  // PROMO arc so it stays inside a 14px row. The full stack keeps the tails.
  const ribbonW = Math.round(size * (compact ? 1.35 : 1.85));
  const ribbonH = Math.round(ribbonW * (compact ? 13 / 61 : 28 / 61));
  const gemSize = compact ? Math.max(10, Math.round(size * 0.72)) : size;
  const overlap = Math.round(ribbonH * (compact ? 0.35 : 0.28));
  const boxW = Math.max(gemSize, ribbonW);
  const boxH = ribbonH + gemSize - overlap;

  return (
    <span
      role="img"
      aria-label={title}
      title={title}
      style={{
        position: 'relative',
        display: 'inline-block',
        width: boxW,
        height: boxH,
        flexShrink: 0,
        verticalAlign: 'middle',
      }}
    >
      {src ? (
        <RarityAssetImg
          src={src}
          size={gemSize}
          style={{
            position: 'absolute',
            left: '50%',
            bottom: 0,
            transform: 'translateX(-50%)',
          }}
        />
      ) : null}
      <RarityAssetImg
        src={badge}
        width={ribbonW}
        height={ribbonH}
        style={{
          position: 'absolute',
          left: '50%',
          top: 0,
          transform: 'translateX(-50%)',
          zIndex: 1,
        }}
      />
    </span>
  );
}

/** Card-level rarity icon: origin gem + promo ribbon on OPP/PR/JDG prints. */
export function CardRarityIcon({
  card,
  size = 18,
  siblings,
  compact = false,
}: {
  card: RarityCardRef;
  size?: number;
  siblings?: readonly RarityCardRef[];
  /** Fit the crown into `size` so it lines up with other badge glyphs. */
  compact?: boolean;
}) {
  const gem = cardRarityGemLabel(card, siblings);
  const promo = cardShowsPromoRarityCrown(card);
  if (!gem && !promo) return null;
  return <RarityGemIcon value={gem ?? ''} size={size} promo={promo} compact={compact} />;
}

// Thin wrappers keep imports stable for any code still referencing gem component names.
export function CommonGem({ size = 18 }: IconSizeProps) {
  return <RarityGemIcon value="Common" size={size} />;
}
export function UncommonGem({ size = 18 }: IconSizeProps) {
  return <RarityGemIcon value="Uncommon" size={size} />;
}
export function RareGem({ size = 18 }: IconSizeProps) {
  return <RarityGemIcon value="Rare" size={size} />;
}
export function EpicGem({ size = 18 }: IconSizeProps) {
  return <RarityGemIcon value="Epic" size={size} />;
}
export function ShowcaseGem({ size = 18 }: IconSizeProps) {
  return <RarityGemIcon value="Showcase" size={size} />;
}

/** Look up the rarity image for autocomplete / lists. Returns null when no asset exists. */
export function getRarityIcon(rarity: string, size = 18): ReactNode | null {
  if (rarity.trim().toLowerCase() === 'promo') {
    return <RarityGemIcon value="" size={size} promo />;
  }
  const src = rarityIconSrc(rarity);
  if (!src) return null;
  return <RarityGemIcon value={rarity} size={size} />;
}

// ─── Card-type symbol (WebP/PNG asset) ───────────────────────────────────────

const CARD_TYPE_SLUGS: Record<string, string> = {
  gear:        'gear.webp',
  unit:        'unit.webp',
  spell:       'spell.png',
  battlefield: 'battlefield.png',
  legend:      'legend.webp',
  rune:        'rune_white.webp',
};

function cardSymbolsPrefix(): string {
  const base = import.meta.env.BASE_URL;
  return base.endsWith('/') ? base : `${base}/`;
}

/** Helmet glyph for Chosen Champion / Champion supertype units (`public/card-symbols/champion-unit.png`). */
export function championUnitIconSrc(): string {
  return `${cardSymbolsPrefix()}card-symbols/champion-unit.png`;
}

/** Public URL for a card-type symbol, or null if no asset exists for that type. */
export function cardTypeIconSrc(cardType: string): string | null {
  const slug = CARD_TYPE_SLUGS[cardType.trim().toLowerCase()];
  if (!slug) return null;
  return `${cardSymbolsPrefix()}card-symbols/${slug}`;
}

/** Card-type icon URL; Champion units use the dedicated helmet asset. */
export function cardTypeIconSrcForCard(card: ChampionCardRef): string | null {
  if (isChampionUnitCard(card)) return championUnitIconSrc();
  return cardTypeIconSrc(card.cardType ?? '');
}

/**
 * White silhouette from `public/card-symbols/`, filled with the surrounding text color.
 * The asset's transparent holes stay open.
 */
export function CardSymbolGlyph({
  src,
  size,
  title,
}: {
  src: string;
  /** Pixel size, or a CSS length such as `1.2em` for inline rules text. */
  size: number | string;
  title?: string;
}) {
  const length = typeof size === 'number' ? `${size}px` : size;
  const mask: CSSProperties = {
    WebkitMaskImage: `url(${src})`,
    maskImage: `url(${src})`,
    WebkitMaskRepeat: 'no-repeat',
    maskRepeat: 'no-repeat',
    WebkitMaskPosition: 'center',
    maskPosition: 'center',
    WebkitMaskSize: 'contain',
    maskSize: 'contain',
  };
  return (
    <span
      title={title}
      aria-hidden
      style={{
        display: 'inline-block',
        width: length,
        height: length,
        flexShrink: 0,
        verticalAlign: 'middle',
        backgroundColor: 'currentColor',
        ...mask,
      }}
    />
  );
}

/**
 * Card-type glyph from `public/card-symbols/`.
 * Returns null for unknown types.
 */
export function CardTypeIcon({ value, size = 18 }: { value: string; size?: number }) {
  const src = cardTypeIconSrc(value);
  if (!src) return null;
  return <CardSymbolGlyph src={src} size={size} title={value} />;
}

/** Champion unit helmet glyph (`public/card-symbols/champion-unit.png`). */
export function ChampionUnitIcon({ size = 18, title = 'Champion unit' }: IconSizeProps & { title?: string }) {
  return (
    <img
      src={championUnitIconSrc()}
      alt=""
      width={size}
      height={size}
      loading="lazy"
      decoding="async"
      draggable={false}
      style={{ display: 'block', objectFit: 'contain', flexShrink: 0 }}
      title={title}
    />
  );
}

// ─── Domain icon (PNG asset) ──────────────────────────────────────────────────

/** Build the public URL for a domain icon, respecting Vite's base path. */
export function domainIconSrc(slug: string): string {
  const base = import.meta.env.BASE_URL;
  const prefix = base.endsWith('/') ? base : `${base}/`;
  return `${prefix}domain-icons/${slug}.png`;
}

interface DomainIconProps {
  value: string;
  size?: number;
}

/** Render the domain glyph image. Returns null if the value isn't a recognised domain. */
export function DomainIcon({ value, size = 18 }: DomainIconProps) {
  const slug = value.toLowerCase();
  if (!['body', 'calm', 'chaos', 'fury', 'mind', 'order'].includes(slug)) return null;
  return (
    <img
      src={domainIconSrc(slug)}
      alt=""
      width={size}
      height={size}
      loading="lazy"
      decoding="async"
      draggable={false}
      style={{ display: 'block', objectFit: 'contain', flexShrink: 0 }}
      title={value}
    />
  );
}
